import React, { useEffect, useState } from 'react';
import {
  Modal,
  Tabs,
  Table,
  Form,
  message,
  Row,
  Spin,
  Col,
  Divider,
  Checkbox,
} from 'antd';
import { OnlineInventoryAPI } from '../../api/onlineInventoryAPI';
import { capitalizeFirstLetter } from '../../../../utils/format-letter';

interface OnlineInventoryDetailViewProps {
  visible: boolean;
  onClose: () => void;
  record: any;
  inventoryCode: string; 
}

const { TabPane } = Tabs;

const OnlineInventoryDetailView: React.FC<OnlineInventoryDetailViewProps> = ({
  visible,
  onClose,
  record,
  inventoryCode
}) => {
  const [assetData, setAssetData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState<any[]>([]);

  useEffect(() => {
    if (!record) return;

    const fetchAssetData = async () => {
      try {
        setLoading(true);
        // Obtener detalles del activo
        const data = await OnlineInventoryAPI.fetchAssetDetails(record.id);
        setAssetData(data);

        // Obtener historial de la solicitud
        const assetHistory = await OnlineInventoryAPI.fetchAssetPetitions(
          record.id
        );
        setHistoryData(assetHistory || []);
      } catch (error) {
        console.error('Error fetching asset details:', error);
        message.error('Error al cargar los detalles del activo');
      } finally {
        setLoading(false);
      }
    };

    fetchAssetData();
  }, [record]);

  const historyColumns = [
    { title: 'Número de ticket', dataIndex: 'ticket', key: 'ticket' },
    {
      title: 'Fecha',
      dataIndex: 'query_date',
      key: 'query_date',
    },
    {
      title: 'Solicitante',
      dataIndex: 'requester',
      key: 'requester',
    },
    {
      title: 'Fecha de reporte',
      dataIndex: 'report_date',
      key: 'report_date',
    },
    { title: 'Tipo de ticket', dataIndex: 'ticket_type', key: 'ticket_type' },
    { title: 'Estado del bien', dataIndex: 'asset_status', key: 'asset_status' },
    { title: 'Estado del invetario', dataIndex: 'status', key: 'status' },
  ];

  return (
    <Modal
      title={`Detalle de activos inventario - ${inventoryCode || 'N/A'}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
    >
      {loading ? (
        <Row justify="center" align="middle" style={{ minHeight: 200 }}>
          <Spin size="large" />
        </Row>
      ) : (
        <Tabs defaultActiveKey="1">
          <TabPane tab="Información general" key="1">
            <h3 style={{ fontWeight: 'bold' }}>Información Básica</h3>
            <Divider style={{ margin: '8px 0' }} />
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <strong>Código/Placa:</strong> {capitalizeFirstLetter(assetData?.plate_code || 'N/A')}
              </Col>
              <Col span={6}>
                <strong>Placa RFID:</strong>{' '}
                {capitalizeFirstLetter(assetData?.rfid_plate_code || 'N/A')}
              </Col>
              <Col span={6}>
                <strong>Placa alterna:</strong>{' '}
                {capitalizeFirstLetter(assetData?.plate_code_alt || 'N/A')}
              </Col>
              <Col span={6}>
                <strong>Número de serie:</strong>{' '}
                {capitalizeFirstLetter(assetData?.serial_number || 'N/A')}
              </Col>
              <Col span={24}>
                <strong>Descripción:</strong> {capitalizeFirstLetter(assetData?.description || 'N/A')}
              </Col>
            </Row>

            <h3 style={{ fontWeight: 'bold', marginTop: '3rem' }}>
              Clasificación
            </h3>
            <Divider style={{ margin: '8px 0' }} />
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <strong>Grupo de activos:</strong>{' '}
                {capitalizeFirstLetter(assetData?.asset_group?.name || 'N/A')}
              </Col>
              <Col span={8}>
                <strong>Clase de activos:</strong>{' '}
                {capitalizeFirstLetter(assetData?.asset_class?.name || 'N/A')}
              </Col>
              <Col span={8}>
                <strong>Artículo:</strong> {capitalizeFirstLetter(assetData?.item?.name || 'N/A')}
              </Col>
            </Row>
            {assetData?.vehicle && Object.keys(assetData.vehicle).length > 0 && (
  <Row style={{ marginTop: '8px' }}>
    <Col span={24}>
      <Checkbox checked disabled>¿es un vehículo?</Checkbox>
    </Col>
  </Row>
)}


            <h3 style={{ fontWeight: 'bold', marginTop: '3rem' }}>
              Ubicación y Responsable
            </h3>
            <Divider style={{ margin: '8px 0' }} />
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <strong>Responsable:</strong>{' '}
                {capitalizeFirstLetter(assetData?.responsible?.name +
                  ' ' +
                  assetData?.responsible?.last_name || 'N/A')}
              </Col>
              <Col span={8}>
                <strong>Centro de costos:</strong>{' '}
                {capitalizeFirstLetter(assetData?.cost_center?.name || 'N/A')}
              </Col>
              <Col span={8}>
                <strong>Ubicación física del bien:</strong>{' '}
                {capitalizeFirstLetter(assetData?.location?.name || 'N/A')}
              </Col>
              <Col span={8}>
                <strong>Estado del bien:</strong>{' '}
                {capitalizeFirstLetter(assetData?.good_status?.status || 'N/A')}
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Historial de la solicitud" key="3">
            <Table
              columns={historyColumns}
              dataSource={historyData}
              pagination={false}
              rowKey="ticket"
            />
          </TabPane>
        </Tabs>
      )}
    </Modal>
  );
};

export default OnlineInventoryDetailView;
